// Puzzle details (DEVELOPMENT.md: Screens): rules, score and its breakdown, source and setter,
// tags, both people's status (hidden-times rule), collections, the SudokuPad link; for
// needs-a-plugin puzzles, "Copy request for Claude Code" (admin). No mockup: it reuses the
// Settings cards and the Library's rows and chips.
import {
  CONSTRAINT_NAMES,
  gridKinds,
  type PuzzleAbout,
  type PuzzleDetail,
  type TechniqueUse,
} from '@zinc/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { Page } from '../components/AppShell';
import { ScoreChip } from '../components/ScoreChip';
import { StatusLine } from '../components/StatusLine';
import { Thumbnail } from '../components/Thumbnail';
import { CollectionTicks } from '../components/library/CollectionTicks';
import { RuleNames } from '../components/solve/RulesStrip';
import { ChevronDownIcon, PlayTriangleIcon, StarIcon } from '../components/icons';
import { Card, ScreenHeader, SectionTitle } from '../components/ui';
import { api } from '../lib/api';
import { core, coreAvailable } from '../lib/core/client';
import { sourceName, typeName, useFavourite } from '../lib/library';
import { useMe } from '../lib/queries';

/** "hidden-single" → "Hidden single"; a layer-2 step names its rule. */
export function techniqueName(t: Pick<TechniqueUse, 'technique' | 'rule'>): string {
  if (t.technique === 'rule' && t.rule) return `${typeName(t.rule)} rule`;
  const words = t.technique.replace(/-/g, ' ');
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function host(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, '');
  } catch {
    return url;
  }
}

function useCopy(): [string | null, (key: string, text: () => Promise<string | null>) => void] {
  const [done, setDone] = useState<string | null>(null);
  const copy = (key: string, text: () => Promise<string | null>) => {
    void text()
      .then(async (t) => {
        if (!t) throw new Error('nothing to copy');
        await navigator.clipboard?.writeText(t);
        setDone(key);
      })
      .catch(() => setDone(`${key}:failed`));
    window.setTimeout(() => setDone((d) => (d?.startsWith(key) ? null : d)), 2500);
  };
  return [done, copy];
}

const ROW = 'flex min-h-12 items-center gap-3 px-3.5 text-[15px]';
const DIVIDER = 'border-b border-solid border-line-soft';

export function PuzzleDetailsPage() {
  const { id = '' } = useParams();
  const me = useMe();
  const otherName = me.data?.other?.displayName ?? null;
  const puzzle = useQuery({
    queryKey: ['puzzle', id],
    queryFn: () => api<PuzzleDetail>(`/api/puzzles/${id}`),
  });
  const about = useQuery({
    queryKey: ['puzzle-about', id],
    queryFn: () => api<PuzzleAbout>(`/api/puzzles/${id}/about`),
  });
  const favourite = useFavourite();
  // The breakdown names the hardest steps, so it stays shut until asked for (Marko: spoilers).
  const [scoreOpen, setScoreOpen] = useState(false);
  const [copied, copy] = useCopy();

  if (puzzle.isError) {
    return (
      <>
        <ScreenHeader title="Puzzle" back="/library" />
        <Page>
          <Card className="px-4 py-5 text-[15px]">
            {puzzle.error.message || 'This puzzle isn’t in the library.'}
          </Card>
        </Page>
      </>
    );
  }
  const p = puzzle.data;
  const a = about.data;
  if (!p) return <ScreenHeader title="" back="/library" />;

  const doc = p.document;
  const known = p.constraintTypes.filter((t) => t in CONSTRAINT_NAMES);
  const unknown = p.constraintTypes.filter((t) => !(t in CONSTRAINT_NAMES));
  const tags = [
    ...new Set([
      // Unmapped kinds count as rules here, so a needs-a-plugin puzzle isn't called classic.
      ...gridKinds(doc, p.constraintTypes).map((k) => typeName(k)),
      ...known.map((t) => typeName(t)),
    ]),
    `${p.rows}×${p.cols}`,
    p.origin === 'generated' ? 'Generated' : 'Hand-set',
  ];
  const breakdown = p.scoreBreakdown;
  const playLabel =
    p.mine.progress === 'in-progress' ? 'Continue' : p.mine.progress === 'solved' ? 'Open' : 'Play';
  const isFavourite = a?.favourite ?? false;

  return (
    <>
      <ScreenHeader title={p.title} back={true} />
      <Page>
        <Card className="flex gap-3.5 p-3.5">
          <Link to={`/puzzle/${p.id}`} tabIndex={-1} aria-hidden="true" className="shrink-0">
            <Thumbnail doc={{ ...doc, solution: null }} size={132} />
          </Link>
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <span className="text-[13px] text-muted">
              {[p.setter && `By ${p.setter}`, sourceName(p.source)].filter(Boolean).join(' · ')}
            </span>
            <span className="self-start">
              <ScoreChip score={p.status === 'needs-plugin' ? null : p.score} />
            </span>
            <div className="flex flex-col gap-1 text-[13px]">
              <StatusLine who="You" status={p.mine} own />
              {otherName && <StatusLine who={otherName} status={p.other} />}
            </div>
            <div className="mt-auto flex gap-2">
              <Link
                to={`/puzzle/${p.id}`}
                className="flex h-11 flex-1 items-center justify-center gap-2 rounded-[12px] bg-accent text-[15px] font-semibold text-white no-underline hover:text-white"
              >
                <PlayTriangleIcon />
                {playLabel}
              </Link>
              <button
                type="button"
                aria-label={isFavourite ? 'Remove from favourites' : 'Add to favourites'}
                aria-pressed={isFavourite}
                disabled={!a}
                onClick={() => favourite.mutate({ id: p.id, favourite: !isFavourite })}
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[12px] border border-line bg-surface p-0 ${isFavourite ? 'text-star' : 'text-icon-soft'}`}
              >
                <StarIcon filled={isFavourite} />
              </button>
            </div>
          </div>
        </Card>

        {p.status === 'needs-plugin' && (
          <Card className="flex flex-col gap-2 px-3.5 py-3 text-[14px]">
            <span className="flex items-center gap-1.5 font-semibold text-warn-ink">
              <span aria-hidden="true" className="h-2 w-2 rounded-[2px] bg-warn" />
              Needs a plugin · playable without checking
            </span>
            <span className="text-muted">
              Some of its rules aren’t built in yet
              {unknown.length ? ` (${unknown.join(', ')})` : ''}, so the app can’t check your digits
              or score it.
            </span>
            {a?.pluginRequest && (
              <button
                type="button"
                onClick={() => copy('request', () => Promise.resolve(a.pluginRequest))}
                className="h-11 self-start rounded-btn border border-line bg-surface px-4 text-[14px] font-semibold text-ink"
              >
                {copied === 'request'
                  ? 'Copied'
                  : copied === 'request:failed'
                    ? 'Couldn’t copy'
                    : 'Copy request for Claude Code'}
              </button>
            )}
          </Card>
        )}

        <section className="flex flex-col gap-2">
          <SectionTitle>Rules</SectionTitle>
          <Card className="px-3.5 py-2 text-[15px]">
            <RuleNames doc={doc} />
          </Card>
          <ul className="m-0 flex list-none flex-wrap gap-1.5 p-0" aria-label="Tags">
            {tags.map((t) => (
              <li
                key={t}
                className="rounded-full border border-line bg-surface px-2.5 py-1 text-[12px] font-semibold text-subtle"
              >
                {t}
              </li>
            ))}
          </ul>
        </section>

        {breakdown && p.status !== 'needs-plugin' && (
          <section className="flex flex-col gap-2">
            <SectionTitle>Score</SectionTitle>
            <Card className="flex flex-col overflow-hidden">
              <button
                type="button"
                aria-expanded={scoreOpen}
                onClick={() => setScoreOpen((o) => !o)}
                className={`${ROW} border-0 bg-transparent text-left text-ink ${scoreOpen ? DIVIDER : ''}`}
              >
                <span className="flex-1">
                  {scoreOpen ? 'Hide the breakdown' : 'Show the breakdown'}
                </span>
                <span
                  className={`text-muted transition-transform ${scoreOpen ? 'rotate-180' : ''}`}
                >
                  <ChevronDownIcon size={18} />
                </span>
              </button>
              {scoreOpen && (
                <>
                  <div className={`${ROW} ${DIVIDER}`}>
                    <span className="flex-1">Hardest step</span>
                    <span className="font-semibold">{techniqueName(breakdown.hardest)}</span>
                  </div>
                  <div className={`${ROW} ${DIVIDER}`}>
                    <span className="flex-1">Steps</span>
                    <span className="font-semibold tabular-nums">
                      {breakdown.steps}
                      {breakdown.hardSteps ? ` · ${breakdown.hardSteps} hard` : ''}
                    </span>
                  </div>
                  {breakdown.longestChain > 0 && (
                    <div className={`${ROW} ${DIVIDER}`}>
                      <span className="flex-1">Longest chain</span>
                      <span className="font-semibold tabular-nums">
                        {breakdown.longestChain} rounds
                      </span>
                    </div>
                  )}
                  <ul
                    className="m-0 flex list-none flex-col gap-1 px-3.5 py-3"
                    aria-label="Techniques"
                  >
                    {[...breakdown.techniques]
                      .sort((x, y) => y.cost - x.cost)
                      .map((t) => (
                        <li
                          key={`${t.technique}:${t.rule ?? ''}`}
                          className="flex items-center gap-2 text-[14px]"
                        >
                          <span className="flex-1">{techniqueName(t)}</span>
                          <span className="text-muted tabular-nums">×{t.count}</span>
                          <ScoreChip score={t.cost} />
                        </li>
                      ))}
                  </ul>
                </>
              )}
            </Card>
          </section>
        )}

        <section className="flex flex-col gap-2">
          <SectionTitle>Collections</SectionTitle>
          <CollectionTicks puzzleId={p.id} />
        </section>

        <section className="flex flex-col gap-2">
          <SectionTitle>Source</SectionTitle>
          <Card className="flex flex-col overflow-hidden">
            {(a?.links ?? []).map((l) => (
              <a
                key={l.url}
                href={l.url}
                target="_blank"
                rel="noreferrer"
                className={`${ROW} ${DIVIDER} no-underline`}
              >
                <span className="flex min-w-0 flex-1 flex-col">
                  <span className="truncate text-ink">{l.title || sourceName(l.source)}</span>
                  <span className="truncate text-[12px] text-muted">{host(l.url)}</span>
                </span>
              </a>
            ))}
            <button
              type="button"
              onClick={() =>
                copy('link', () => (coreAvailable() ? core.exportLink(doc) : Promise.resolve(null)))
              }
              className={`${ROW} border-0 bg-transparent text-left font-semibold text-accent`}
            >
              {copied === 'link'
                ? 'SudokuPad link copied'
                : copied === 'link:failed'
                  ? 'Couldn’t make a SudokuPad link here'
                  : 'Copy SudokuPad link'}
            </button>
          </Card>
        </section>
      </Page>
    </>
  );
}
